import { FC, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';
import Drawer from './Drawer';
import Header from './Header';
import useConfig from '../../hooks/useConfig';
import { useMenu } from '../../store/menu';
import ErrorBoundaryWithRouter from '../../errorBoundary/ErrorBoundary';
import { ThemeMode } from '../../types/config';
import { DrawerComponent, DrawerContextProvider } from '../../drawer/DrawerContext';
import { ModalComponent, ModalContextProvider } from '../../modal/ModalContext';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import { useCookies } from 'react-cookie';

const MainLayout: FC = () => {
	const theme = useTheme();
	const matchDownXL = useMediaQuery(theme.breakpoints.down('xl'));
	const { pathname } = useLocation();
	const [{ ood_impersonated_token }] = useCookies(['ood_impersonated_token']);
	const { openDrawer } = useMenu();

	const { miniDrawer } = useConfig();

	// set media wise responsive drawer
	useEffect(() => {
		if (!miniDrawer) {
			openDrawer(!matchDownXL);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [matchDownXL]);

	const impersonationHighlight =
		ood_impersonated_token != null ? { border: `12px solid ${theme.palette.primary.main}`, borderRight: 'none', borderBottom: 'none' } : {};

	return (
		<ErrorBoundaryWithRouter>
			<ModalContextProvider>
				<DrawerContextProvider>
					<Box
						sx={{
							gap: 0,
							flex: '1 1 auto',
							minHeight: 0,
							gridAutoFlow: 'column',

							'::before': {
								content: '""',
								width: '100%',
								height: '100%',
								display: 'block',
								position: 'absolute',
								inset: 0,
								pointerEvents: 'none',
								zIndex: 5000,
								...impersonationHighlight,
							},
						}}
						display={'grid'}
						gridTemplateColumns={{ xs: '1fr', lg: 'auto 1fr' }}
						gridTemplateRows={'max(60px) max(100svh - 60px)'}
						gridTemplateAreas={{
							xs: `
								"menu"
								"content"
							`,
							lg: `
							"sidebar menu"
							"sidebar content"
						`,
						}}
					>
						<Header />
						<Drawer />

						<Box
							component={'main'}
							sx={{
								gridArea: 'content',
								height: '100%',
								overflowY: 'auto',
								position: 'relative',
								backgroundColor: (theme) =>
									pathname.includes('/settings') ? (theme.palette.mode === ThemeMode.DARK ? alpha('#f7f8fa', 0.02) : '#f7f8fa') : null,
							}}
							bgcolor={theme.palette[getNightOrDayPalette(theme)][0]}
						>
							<>
								<Outlet />
								<DrawerComponent />
							</>
						</Box>
					</Box>
					<ModalComponent />
				</DrawerContextProvider>
			</ModalContextProvider>
		</ErrorBoundaryWithRouter>
	);
};

export default MainLayout;
